// frontend/src/pages/Home/tiles/tileParts.tsx
/**
 * The pieces every At a glance tile is built from (SA-1171, spec §3.3):
 * a labeled frame whose value links to the list it counts, the loading and
 * failed states, a fourteen-bar sparkline and a meter with a tick. Neutral
 * tokens only (parent spec §10): a tile is informational. Module tiles
 * import these as module widgets import `widgetParts`.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { READ_UNAVAILABLE, type ReadState } from '@/components/records/readState'

export { isUnavailable } from '@/pages/Home/widgetParts'

export interface TileFrameProps {
  id: string
  title: string
  value: ReactNode
  /** The list the value counts (parent spec D4). Absent on a dash. */
  to?: string
  note?: ReactNode
  /** The overdue value above zero (spec §4): `--warning-strong`. */
  warn?: boolean
  children?: ReactNode
}

export function TileFrame({ id, title, value, to, note, warn = false, children }: TileFrameProps) {
  return (
    <div role="group" aria-label={title} data-tile={id} className="flex min-w-0 flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-[.08em] text-muted-foreground">{title}</span>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        {/* Proportional figures, never tabular-nums (parent spec §5.2). */}
        <span className={`text-[26px] font-semibold leading-none tracking-tight ${warn ? 'text-warning-strong' : ''}`}>
          {to ? <Link to={to} className="hover:underline">{value}</Link> : value}
        </span>
        {note != null && note !== '' && <span className="text-[12.5px] text-muted-foreground">{note}</span>}
      </div>
      {children}
    </div>
  )
}

/** The loading and fault fallback: the label over one dash. */
export function TileDash({ id, title }: { id: string; title: string }) {
  return <TileFrame id={id} title={title} value="—" />
}

/** A failed read with nothing cached (parent spec §11): the label, a dash and the one word. */
export function TileUnavailable({ id, title, read: _read }: { id: string; title: string; read: ReadState<unknown> & { error?: Error | null } }) {
  return (
    <TileFrame id={id} title={title} value="—"
      note={<span role="status">{READ_UNAVAILABLE}</span>} />
  )
}

const SPARK_W = 100
const SPARK_H = 24
const SPARK_GAP = 1

/** Fourteen bars (one per point), the last in the foreground, the rest
 * muted at 35 %; no axis, no numbers (parent spec §5.2). A zero series draws
 * hairline bars. `label` names the series and reads the numbers out. */
export function Sparkline({ points, label }: { points: number[]; label: string }) {
  const max = Math.max(1, ...points)
  const bar = (SPARK_W - SPARK_GAP * Math.max(0, points.length - 1)) / Math.max(1, points.length)
  return (
    <svg role="img" aria-label={label} viewBox={`0 0 ${SPARK_W} ${SPARK_H}`} preserveAspectRatio="none" className="mt-1 h-6 w-full" data-testid="sparkline">
      {points.map((v, i) => {
        const h = Math.max(1, (v / max) * SPARK_H)
        const last = i === points.length - 1
        return (
          <rect key={i} x={i * (bar + SPARK_GAP)} y={SPARK_H - h} width={bar} height={h}
            className={last ? 'fill-foreground' : 'fill-muted-foreground opacity-35'} />
        )
      })}
    </svg>
  )
}

const clamp = (x: number) => (Number.isFinite(x) ? Math.min(1, Math.max(0, x)) : 0)

/** A `--muted` track, a `--foreground` fill at `fraction`, a one-pixel
 * `--muted-foreground` tick at `tick` (today's share of the month; never red,
 * parent spec §5.2). Both clamped to [0, 1]; a non-finite value reads as 0. */
export function Meter({ fraction, tick, label }: { fraction: number; tick: number; label: string }) {
  return (
    <div role="img" aria-label={label} data-testid="meter" className="relative mt-1 h-2 w-full overflow-hidden rounded bg-muted">
      <i className="absolute inset-y-0 left-0 block bg-foreground" style={{ width: `${clamp(fraction) * 100}%` }} data-testid="meter-fill" />
      <i className="absolute inset-y-0 block w-px bg-muted-foreground" style={{ left: `${clamp(tick) * 100}%` }} data-testid="meter-tick" />
    </div>
  )
}
